import { useCallback, useState } from 'react';

import { Stars01Icon } from '@/components/icons/Stars01Icon';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { ProminentActionBanner } from '@/components/notifications/ProminentActionBanner/ProminentActionBanner';
import { useReportError } from '@/hooks/useReportError';

import { useClearAutoOnboardedRecordsMutation } from './ClientPeopleAutoOnboardedBanner.generated';

interface ClientPeopleAutoOnboardedBannerProps {
  householdId: string;
  /** Household.newIndividualsAutomaticallyOnboardedAt; non-null when Luminary
   * auto-onboarded individuals from uploaded documents. */
  newIndividualsAutomaticallyOnboardedAt?: Date | null;
}

/**
 * Banner shown at the top of the People page when Luminary has automatically
 * onboarded individuals from uploaded documents.
 *
 * Dismissing hides the banner immediately and clears the timestamp
 * server-side so it stays dismissed across reloads and for all users of the
 * household.
 */
export function ClientPeopleAutoOnboardedBanner({
  householdId,
  newIndividualsAutomaticallyOnboardedAt,
}: ClientPeopleAutoOnboardedBannerProps) {
  const { reportError } = useReportError();
  const { showFeedback } = useFeedback();

  const [isLocallyDismissed, setIsLocallyDismissed] = useState(false);

  const [clearAutoOnboarded] = useClearAutoOnboardedRecordsMutation({
    onError: (error) => {
      // The server timestamp was never cleared, so roll back the optimistic
      // dismissal — otherwise the banner stays hidden until remount with no
      // way to retry, then reappears on reload.
      setIsLocallyDismissed(false);
      reportError('Failed to dismiss auto-onboarded records banner', error);
      showFeedback('Failed to dismiss. Please try again.');
    },
  });

  const handleDismiss = useCallback(() => {
    // Hide immediately; the mutation clears the timestamp so it stays hidden.
    setIsLocallyDismissed(true);
    void clearAutoOnboarded({ variables: { householdId } });
  }, [clearAutoOnboarded, householdId]);

  const shouldShow =
    !isLocallyDismissed && Boolean(newIndividualsAutomaticallyOnboardedAt);

  if (!shouldShow) {
    return null;
  }

  return (
    <ProminentActionBanner
      icon={Stars01Icon}
      header="People and organizations added automatically"
      description="Luminary AI added people and organizations from your documents. You can edit their details below."
      action={{ label: 'Dismiss', onClick: handleDismiss }}
    />
  );
}
